import { getFirestore, collection, query, orderBy, getDocs } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { PaginationControls } from "../../components/common/PaginationControls.js";

const db = getFirestore(app);

export const LikesTab = async (target, user) => {
    const q = query(collection(db, `users/${user.uid}/likes`), orderBy('createdAt', 'desc'));
    const snapped = await getDocs(q);

    if (snapped.empty) {
        renderEmptyState(target, "찜한 작품이 없습니다.", "마음에 드는 작품에 하트를 눌러보세요!");
        return;
    }

    // [FIX v1.36.411] Fetch Fresh Series Data
    // We cannot trust the snapshot data in 'likes' collection (it's stale).
    // Extract IDs and fetch current series data.
    const seriesIds = new Set();
    const likesMap = new Map(); // seriesId -> likeData (for createdAt sort key if needed)

    snapped.forEach(doc => {
        const data = doc.data();
        const sid = data.seriesId || data.id; // Fallback to doc ID if seriesId missing
        if (sid) {
            seriesIds.add(sid);
            likesMap.set(sid, { ...data, id: doc.id }); // Keep original doc ID for keys if needed
        }
    });

    if (seriesIds.size === 0) {
        renderEmptyState(target, "찜한 작품이 없습니다.", "마음에 드는 작품에 하트를 눌러보세요!");
        return;
    }

    // Batch Fetch
    const { ContentService } = await import("../../services/content.service.js");
    let freshSeries = await ContentService.getSeriesByIds(Array.from(seriesIds));

    // Filter out deleted/unpublished (getSeriesByIds returns null for missing, but we filter nulls there. Double check 'status' here)
    // Also, we want to maintain the ORDER of 'likes' (Most recent first).
    // 'freshSeries' comes back in arbitrary order (likely ID order or fetch order).

    // 1. Create Lookup
    const freshMap = new Map();
    freshSeries.forEach(s => freshMap.set(s.id, s));

    // 2. [B] Group by Series — count episodes, keep latest timestamp
    const groupMap = new Map(); // seriesId -> { fresh, count, latestAt }
    snapped.forEach(doc => {
        const data = doc.data();
        const sid = data.seriesId || data.id;
        const fresh = freshMap.get(sid);

        if (fresh && fresh.status === 'published') {
            const likedAt = data.createdAt;
            if (groupMap.has(sid)) {
                const g = groupMap.get(sid);
                g.count++;
                // Keep the most recent like timestamp
                if (likedAt && (!g.latestAt || likedAt.toMillis() > g.latestAt.toMillis())) {
                    g.latestAt = likedAt;
                }
            } else {
                groupMap.set(sid, { fresh, count: 1, latestAt: likedAt });
            }
        }
    });

    // 3. Sort by most recent like, build final list
    const allLikes = Array.from(groupMap.values())
        .sort((a, b) => {
            const tA = a.latestAt ? a.latestAt.toMillis() : 0;
            const tB = b.latestAt ? b.latestAt.toMillis() : 0;
            return tB - tA;
        })
        .map(g => ({
            ...g.fresh,
            episodeLikeCount: g.count,
            likedAt: g.latestAt
        }));

    if (allLikes.length === 0) {
        renderEmptyState(target, "찜한 작품이 없습니다.", "마음에 드는 작품에 하트를 눌러보세요!");
        return;
    }

    // Internal State
    let currentPage = 1;

    const updateView = (page) => {
        const itemsPerPage = 12; // 3x4 Grid
        const totalItems = allLikes.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);

        if (page < 1) page = 1;
        if (page > totalPages) page = totalPages;
        currentPage = page;

        const startIdx = (currentPage - 1) * itemsPerPage;
        const pageItems = allLikes.slice(startIdx, startIdx + itemsPerPage);

        renderGrid(pageItems);

        if (totalPages > 1) {
            PaginationControls.render({
                target: target.querySelector('#likes-pagination-container'),
                currentPage: currentPage,
                totalPages: totalPages,
                onPageChange: (newPage) => window.LikesActions.setPage(newPage)
            });
        }
    };

    const renderGrid = (items) => {
        let html = '<div class="grid grid-cols-3 gap-4 p-4 animate-fade-in">';
        items.forEach(data => {
            let thumb = data.coverImage || data.thumbnail || 'https://placehold.co/200x300?text=No+Img';
            // Simple Proxy Logic
            if (thumb && (thumb.includes('pstatic.net') || thumb.includes('naver.com') || thumb.includes('daumcdn.net'))) {
                thumb = "https://wsrv.nl/?url=" + encodeURIComponent(thumb) + "&w=400&output=webp";
            }

            html += `
                <div class="flex flex-col gap-2 cursor-pointer group relative pointer-events-auto" role="button" onclick="window.router.navigate('/series/${data.id}')">
                    <div class="aspect-[3/4] rounded-lg overflow-hidden bg-gray-200 shadow-sm relative transition-transform group-hover:scale-[1.02]">
                        <img src="${thumb}" class="w-full h-full object-cover" style="-webkit-touch-callout: none;" onerror="this.onerror=null; this.src='https://placehold.co/200x300?text=No+Img';">
                        <div class="absolute bottom-2 right-2 drop-shadow-md">
                            <span class="material-symbols-outlined text-2xl text-red-600" style="font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;">favorite</span>
                        </div>
                    </div>
                    <h4 class="text-sm font-bold text-gray-900 truncate leading-tight">${data.title}</h4>
                </div>
            `;
        });
        html += '</div>';

        // Clear previous content but keep pagination container if exists? No, easier to wipe.
        // But we need a container structure.
        const gridContainer = target.querySelector('#likes-grid-container');
        if (gridContainer) gridContainer.innerHTML = html;
        else target.innerHTML = `<div id="likes-grid-container">${html}</div><div id="likes-pagination-container"></div>`;
    };

    // Global Actions
    window.LikesActions = {
        setPage: (p) => {
            updateView(p);
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    updateView(1);
};

const renderEmptyState = (target, title, subtitle, action) => {
    target.innerHTML = `
        <div class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                <span class="material-symbols-outlined text-4xl">inventory_2</span>
            </div>
            <h3 class="text-gray-900 font-bold mb-1">${title}</h3>
            <p class="text-gray-500 text-sm mb-6">${subtitle}</p>
            ${action ? `<button id="btn-empty-action" class="px-5 py-2 bg-indigo-50 text-indigo-600 font-bold rounded-full text-sm hover:bg-indigo-100 transition-colors">구경하러 가기</button>` : ''}
        </div>
    `;
    if (action) {
        target.querySelector('#btn-empty-action').onclick = action;
    }
};
